import { useEffect, useState } from "react";
import { Check, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { useOnboardingQualification } from "@/hooks/queries/useOnboardingQualification";
import { ActionBar, ActionButton } from "../OnboardingShell";
import type { SaleCopy } from "./contractorLine";

// Que la espera no parpadee aunque el cruce ya estuviera listo.
const MIN_WAIT_MS = 1500;
// Pasado esto se ofrece ver lo que ya está: lo que falte queda "por analizar".
const SLOW_AFTER_MS = 5 * 60 * 1000;

/**
 * Espera real del cruce de prueba: primero la lectura de documentos, después el
 * cruce de cada licitación. Si faltan las esenciales, lo dice y ofrece cómo seguir.
 */
export function QualifyingStep({
  sale,
  onDone,
  onUploadMore,
  onSkip,
}: {
  sale: SaleCopy;
  onDone: () => void;
  onUploadMore: () => void;
  onSkip: () => void;
}) {
  const { data } = useOnboardingQualification();
  const [startedAt] = useState(() => Date.now());
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (data?.phase !== "done") return;
    const timer = setTimeout(onDone, Math.max(0, MIN_WAIT_MS - (Date.now() - startedAt)));
    return () => clearTimeout(timer);
  }, [data?.phase]);

  if (data?.phase === "missing") {
    return (
      <div className="mx-auto w-full max-w-[620px]">
        <h1 className="text-[30px] font-extrabold leading-[1.1] tracking-[-0.03em] text-slate-900 sm:text-[38px]">
          Nos falta un documento para calcularlo.
        </h1>
        <p className="mt-4 text-[15px] leading-[1.6] text-slate-600">
          Sin {data.missingEssentials.join(" ni ")} casi todo saldría "por revisar". Súbelas y lo
          calculamos, o sigue sin calificación por ahora.
        </p>
        <ActionBar>
          <ActionButton tone="blue" onClick={onUploadMore}>
            Subir lo que falta
          </ActionButton>
          <button
            type="button"
            onClick={onSkip}
            className="cursor-pointer px-3 py-2.5 text-[14px] font-semibold text-slate-500 hover:text-slate-900"
          >
            Seguir sin calificación
          </button>
        </ActionBar>
      </div>
    );
  }

  const phase = data?.phase ?? "reading";
  const total = data?.total ?? 0;
  const steps = [
    { label: "Leyendo tus constancias y tu experiencia", done: phase !== "reading" },
    {
      label:
        phase === "crossing" && total > 0
          ? `Cruzando con las bases de ${total} ${sale.many} abiertas (${data?.done ?? 0} de ${total})`
          : `Cruzando con las bases de ${total || "las"} ${sale.many} abiertas`,
      done: phase === "done",
    },
    { label: "Comparando tu experiencia con cada requisito", done: phase === "done" },
  ];

  return (
    <div className="mx-auto w-full max-w-[720px] flex min-h-[60vh] flex-col justify-center">
      <div className="relative flex size-[76px] items-center justify-center">
        <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-blue-100 border-r-blue-700" />
        <span className="flex size-12 items-center justify-center rounded-full bg-white shadow-sm">
          <Sparkles className="size-5 text-blue-700" />
        </span>
      </div>

      <h1 className="mt-7 max-w-[560px] text-[30px] font-extrabold leading-[1.1] tracking-[-0.03em] text-slate-900 sm:text-[38px]">
        Calculando en qué {sale.many} calificas…
      </h1>

      <ul className="mt-6 space-y-3">
        {steps.map((step) => (
          <li key={step.label} className="flex items-center gap-3 text-[15px]">
            <span
              className={cn(
                "flex size-6 shrink-0 items-center justify-center rounded-full transition-colors",
                step.done ? "bg-blue-700 text-white" : "bg-white text-slate-300",
              )}
            >
              <Check className="size-3.5" strokeWidth={3} />
            </span>
            <span className={step.done ? "text-slate-800" : "text-slate-400"}>{step.label}</span>
          </li>
        ))}
      </ul>

      {slow && (
        <div className="mt-8">
          <p className="text-[14px] text-slate-500">
            Está tardando más de lo normal. Puedes ver lo que ya está; el resto queda por analizar.
          </p>
          <button
            type="button"
            onClick={onDone}
            className="mt-3 cursor-pointer rounded-full border border-blue-200 bg-white px-5 py-2.5 text-[14px] font-bold text-blue-700 hover:bg-blue-50"
          >
            Ver lo que ya está
          </button>
        </div>
      )}
    </div>
  );
}
